import { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Avatar, StatTile } from "../components/common";
import DartPad from "../components/DartPad";
import Burst from "../components/Burst";
import { resolveDart, checkoutSuggestion, dartLabel, START_501 } from "../lib/dartEngine";
import { playCheer } from "../lib/fx";
import { Home, Flag, Ghost, Trophy } from "lucide-react";

const BB_MAX_INNING = 9;

function pickGhost(pool, usedIds) {
  const fresh = pool.filter((g) => !usedIds.includes(g.id));
  const from = fresh.length ? fresh : pool;
  return from[Math.floor(Math.random() * from.length)];
}

const scoreOf = (ds) => ds.reduce((s, d) => s + (d.counted ? d.value : 0), 0);

function ghostOwner(cfg) {
  return cfg.ghostName ? `${cfg.ghostName}'s Ghost` : "Ghost";
}

export default function GhostPlay() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const cfg = state;
  const gamesNeeded = cfg ? Math.floor(cfg.bestOf / 2) + 1 : 1;
  const [youWins, setYouWins] = useState(0);
  const [ghostWins, setGhostWins] = useState(0);
  const [gameNo, setGameNo] = useState(1);
  const [used, setUsed] = useState([]);
  const [ghost, setGhost] = useState(() => (cfg?.ghosts?.length ? pickGhost(cfg.ghosts, []) : null));
  const [log, setLog] = useState([]);
  const [over, setOver] = useState(false);

  if (!cfg || !cfg.ghosts?.length) return <Navigate to="/practice" replace />;

  const onGameEnd = (result) => {
    const yw = youWins + (result.youWon ? 1 : 0);
    const gw = ghostWins + (result.youWon ? 0 : 1);
    setLog((l) => [...l, { ghost, gameNo, ...result }]);
    setYouWins(yw);
    setGhostWins(gw);
    if (yw >= gamesNeeded || gw >= gamesNeeded || cfg.bestOf === 1) {
      setOver(true);
      return;
    }
    const nu = [...used, ghost.id];
    setUsed(nu);
    setGhost(pickGhost(cfg.ghosts, nu));
    setGameNo((n) => n + 1);
  };

  if (over) {
    return <GhostSeriesSummary cfg={cfg} log={log} youWins={youWins} ghostWins={ghostWins} navigate={navigate} />;
  }

  const header = (
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8E8E93] flex items-center gap-1.5">
          <Ghost className="w-4 h-4" /> {ghostOwner(cfg)} · {cfg.gameType === "baseball" ? "Baseball" : "501"}
          {cfg.bestOf > 1 && ` · Best of ${cfg.bestOf}`}
        </div>
        <div className="font-display text-3xl font-black uppercase text-white leading-none">
          {cfg.bestOf > 1 ? `Game ${gameNo}` : "Race the Ghost"}
        </div>
      </div>
      <button
        data-testid="ghost-exit-btn"
        onClick={() => navigate("/practice")}
        className="flex items-center gap-1.5 text-sm font-bold uppercase text-slate-400 hover:text-[#FF3B30] transition-colors"
      >
        <Flag className="w-4 h-4" /> Exit
      </button>
    </div>
  );

  const seriesScore = cfg.bestOf > 1 && (
    <div data-testid="ghost-series-score" className="panel rounded-sm p-3 mb-4 flex items-center justify-center gap-6">
      <div className="text-center">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{cfg.playerName}</div>
        <div className="font-display text-3xl font-black text-[#34C759]">{youWins}</div>
      </div>
      <div className="text-slate-600 font-display text-xl">—</div>
      <div className="text-center">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{ghostOwner(cfg)}</div>
        <div className="font-display text-3xl font-black text-[#8E8E93]">{ghostWins}</div>
      </div>
      <div className="text-[10px] text-slate-400 font-bold uppercase">First to {gamesNeeded}</div>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto">
      {header}
      {seriesScore}
      {cfg.gameType === "baseball" ? (
        <GhostBaseballGame key={gameNo} ghost={ghost} cfg={cfg} onGameEnd={onGameEnd} />
      ) : (
        <Ghost501Game key={gameNo} ghost={ghost} cfg={cfg} onGameEnd={onGameEnd} />
      )}
    </div>
  );
}

function Ghost501Game({ ghost, cfg, onGameEnd }) {
  const finishRule = cfg.finishRule || "double_out";
  const startRule = cfg.startRule || "straight_in";
  const [remaining, setRemaining] = useState(START_501);
  const [darts, setDarts] = useState([]);
  const [mult, setMult] = useState(1);
  const [started, setStarted] = useState(false);
  const [yourDarts, setYourDarts] = useState(0);
  const [ghostCursor, setGhostCursor] = useState(0);
  const [ghostRemaining, setGhostRemaining] = useState(START_501);
  const [burst, setBurst] = useState(null);
  const [result, setResult] = useState(null);
  const [yourPoints, setYourPoints] = useState(0);
  const [lastVisit, setLastVisit] = useState(null);

  const used = darts.reduce((s, d) => s + (d.counted ? d.value : 0), 0);
  const live = remaining - used;
  const suggestion = started ? checkoutSuggestion(live, finishRule) : "";
  const needsDoubleIn = startRule === "double_in" && !started;
  const legAvg = yourDarts ? (yourPoints / yourDarts * 3).toFixed(1) : "0.0";

  const advanceGhost = (yourDartCount) => {
    const gt = ghost.turns[ghostCursor];
    const newGhostRem = gt ? Math.max(0, ghostRemaining - (gt.score || 0)) : ghostRemaining;
    setGhostRemaining(newGhostRem);
    setGhostCursor((c) => c + 1);
    if (newGhostRem === 0) {
      setBurst({ label: "GHOST WINS", sub: `${ghost.darts_to_finish} darts`, color: "#8E8E93" });
      setResult({ youWon: false, youValue: null, ghostValue: ghost.darts_to_finish });
    }
  };

  const handleDart = (base, forceMult) => {
    if (result) return;
    const m = forceMult ? forceMult : base === 25 && mult === 3 ? 2 : mult;
    const dart = { base, multiplier: m, value: base * m, label: dartLabel(base, m) };
    const usedCounted = darts.reduce((s, d) => s + (d.counted ? d.value : 0), 0);
    const res = resolveDart(remaining, usedCounted, dart, finishRule, started, startRule);
    dart.counted = res.counted;
    const newDarts = [...darts, dart];
    setMult(1);
    if (res.started && !started) setStarted(true);

    if (res.status === "win") {
      const dartsThisTurn = newDarts.length;
      const finalCount = yourDarts + dartsThisTurn;
      const winScore = scoreOf(newDarts);
      setYourDarts(finalCount);
      setYourPoints((p) => p + winScore);
      setLastVisit({ darts: newDarts, total: winScore, busted: false });
      setDarts([]);
      setBurst({ label: "LEG WON", sub: `${finalCount} darts`, color: "#34C759" });
      playCheer("checkout");
      setResult({ youWon: true, youValue: finalCount, ghostValue: ghost.darts_to_finish });
      return;
    }
    if (res.status === "bust") {
      const newCount = yourDarts + newDarts.length;
      setYourDarts(newCount);
      setLastVisit({ darts: newDarts, total: 0, busted: true });
      setDarts([]);
      advanceGhost(newCount);
      return;
    }
    if (newDarts.length === 3) {
      const score = scoreOf(newDarts);
      const newCount = yourDarts + 3;
      setYourDarts(newCount);
      setYourPoints((p) => p + score);
      setLastVisit({ darts: newDarts, total: score, busted: false });
      if (score === 180) {
        setBurst({ label: "180", color: "#FF3B30" });
        playCheer("180");
      }
      setRemaining(remaining - score);
      setDarts([]);
      advanceGhost(newCount);
      return;
    }
    setDarts(newDarts);
  };

  const undo = () => setDarts(darts.slice(0, -1));

  if (result) {
    return (
      <>
        <GhostGameResult result={result} cfg={cfg} onGameEnd={onGameEnd}
          youLabel={result.youWon ? `${result.youValue} darts` : "Did not finish"}
          ghostLabel={`${ghost.darts_to_finish} darts`} />
        <AnimatePresence>
          {burst && <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />}
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="panel rounded-sm p-4 text-center border-t-2 border-t-[#34C759]">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Avatar name={cfg.playerName} size={24} active />
            <span className="text-xs font-bold uppercase text-slate-200 truncate">{cfg.playerName}</span>
          </div>
          <div data-testid="ghost-you-remaining" className="font-display text-6xl font-black text-white leading-none">{live}</div>
          <div data-testid="ghost-you-avg" className="text-[#007AFF] text-xs font-bold uppercase tracking-wide mt-1">Avg {legAvg}</div>
          {suggestion && <div className="text-[#FFCC00] text-xs font-bold uppercase tracking-wide mt-1">{suggestion}</div>}
          {needsDoubleIn && <div className="text-[#FF9500] text-xs font-bold uppercase mt-1">Double to start</div>}
        </div>
        <div className="panel rounded-sm p-4 text-center border-t-2 border-t-[#8E8E93]">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Ghost className="w-5 h-5 text-[#8E8E93]" />
            <span className="text-xs font-bold uppercase text-slate-400 truncate">{ghostOwner(cfg)}</span>
          </div>
          <div data-testid="ghost-remaining" className="font-display text-6xl font-black text-[#8E8E93] leading-none">{ghostRemaining}</div>
        </div>
      </div>

      <div className="panel rounded-sm p-3 mb-3" data-testid="ghost-last-visit">
        <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-slate-600 mb-1">Your Last Visit</div>
        <div className="flex items-center justify-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <span key={i} data-testid={`ghost-last-visit-dart-${i}`} className="min-w-[44px] px-1.5 py-0.5 rounded-sm bg-[#141414] border border-[#262626] font-display text-sm font-bold text-slate-300 text-center">
              {lastVisit && lastVisit.darts?.[i] ? lastVisit.darts[i].label : "—"}
            </span>
          ))}
          <span data-testid="ghost-last-visit-total" className={`ml-1.5 font-display text-xl font-black ${lastVisit && lastVisit.busted ? "text-[#FF3B30]" : "text-white"}`}>
            {lastVisit ? (lastVisit.busted ? "BUST" : lastVisit.total) : 0}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-3 h-9">
        {[0, 1, 2].map((i) => {
          const d = darts[i];
          return (
            <div key={i} className={`min-w-[64px] px-3 py-1.5 rounded-sm text-center font-display text-lg font-bold border ${d ? "border-[#007AFF] text-white" : "border-[#262626] text-slate-700"}`}>
              {d ? d.label : "—"}
            </div>
          );
        })}
      </div>

      <DartPad mult={mult} setMult={setMult} onDart={handleDart} onUndo={undo} />

      <AnimatePresence>
        {burst && <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />}
      </AnimatePresence>
    </>
  );
}

function GhostBaseballGame({ ghost, cfg, onGameEnd }) {
  const [innings, setInnings] = useState([]);
  const [inningNumber, setInningNumber] = useState(1);
  const [darts, setDarts] = useState([]);
  const [revealed, setRevealed] = useState(0);
  const [burst, setBurst] = useState(null);
  const [result, setResult] = useState(null);

  const target = inningNumber;
  const runsThisTurn = darts.reduce((s, d) => s + d.mult, 0);
  const yourTotal = innings.reduce((s, r) => s + r, 0);
  const ghostTotal = ghost.innings.slice(0, revealed).reduce((s, r) => s + r, 0);
  const ghostGameTotal = ghost.total_runs;

  const commitInning = (finalDarts) => {
    const runs = finalDarts.reduce((s, d) => s + d.mult, 0);
    const newInnings = [...innings, runs];
    const newRevealed = revealed + 1;
    setInnings(newInnings);
    setRevealed(newRevealed);
    setDarts([]);
    if (inningNumber >= BB_MAX_INNING) {
      const yt = newInnings.reduce((s, r) => s + r, 0);
      const gt = ghost.innings.slice(0, newRevealed).reduce((s, r) => s + r, 0);
      const youWon = yt > gt;
      setBurst({ label: youWon ? "YOU WIN" : gt === yt ? "GHOST HOLDS" : "GHOST WINS", sub: `${yt} — ${gt}`, color: youWon ? "#34C759" : "#8E8E93" });
      playCheer(youWon ? "checkout" : "180");
      setResult({ youWon, youValue: yt, ghostValue: gt });
    } else {
      setInningNumber((n) => n + 1);
    }
  };

  const throwDart = (mult) => {
    if (result) return;
    const newDarts = [...darts, { mult }];
    if (newDarts.length === 3) {
      commitInning(newDarts);
      return;
    }
    setDarts(newDarts);
  };

  const undo = () => {
    if (darts.length > 0) setDarts(darts.slice(0, -1));
  };

  if (result) {
    return (
      <>
        <GhostGameResult result={result} cfg={cfg} onGameEnd={onGameEnd}
          youLabel={`${result.youValue} runs`} ghostLabel={`${ghostGameTotal} runs`} />
        <AnimatePresence>
          {burst && <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />}
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <StatTile testid="ghost-you-runs" label={cfg.playerName} value={yourTotal} accent="text-[#34C759]" />
        <StatTile testid="ghost-runs" label={ghostOwner(cfg)} value={ghostTotal} accent="text-[#8E8E93]" />
      </div>

      <div className="panel rounded-sm p-6 mb-4 text-center border-t-2 border-t-[#34C759]">
        <div className="font-display text-2xl font-black uppercase text-white leading-none mb-1">
          Inning {Math.min(inningNumber, BB_MAX_INNING)} of {BB_MAX_INNING}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Target</div>
        <div data-testid="ghost-bb-target" className="font-display text-7xl font-black text-white leading-none">{target}</div>
        <div className="text-sm font-bold uppercase tracking-wide text-[#FFCC00] mt-1">🎯 Aim: T{target}</div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-3 h-10">
        {[0, 1, 2].map((i) => {
          const d = darts[i];
          const lbl = d ? (d.mult === 0 ? "MISS" : `+${d.mult}`) : "—";
          return (
            <div key={i} className={`min-w-[64px] px-3 py-1.5 rounded-sm text-center font-display text-lg font-bold border ${d ? (d.mult > 0 ? "border-[#34C759] text-[#34C759]" : "border-[#FF3B30] text-[#FF3B30]") : "border-[#262626] text-slate-700"}`}>
              {lbl}
            </div>
          );
        })}
      </div>

      <div className="space-y-1">
        <div className="grid grid-cols-3 gap-1">
          <button data-testid="ghost-bb-single" onClick={() => throwDart(1)} className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#34C759] hover:bg-[#2fb350] text-black transition-colors duration-100">Single<span className="block text-xs font-bold opacity-70">+1</span></button>
          <button data-testid="ghost-bb-double" onClick={() => throwDart(2)} className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#007AFF] hover:bg-[#0069d9] text-white transition-colors duration-100">Double<span className="block text-xs font-bold opacity-70">+2</span></button>
          <button data-testid="ghost-bb-triple" onClick={() => throwDart(3)} className="py-6 rounded-sm font-display text-2xl font-black uppercase tracking-wider bg-[#FF3B30] hover:bg-[#e0332a] text-white transition-colors duration-100">Triple<span className="block text-xs font-bold opacity-70">+3</span></button>
        </div>
        <button data-testid="ghost-bb-miss" onClick={() => throwDart(0)} className="w-full py-4 rounded-sm font-display text-2xl font-black uppercase tracking-wider panel text-slate-300 hover:bg-[#1f1f1f] transition-colors duration-100">Miss <span className="text-sm opacity-70">+0</span></button>
        <button data-testid="ghost-bb-undo" onClick={undo} className="w-full py-2.5 rounded-sm font-bold uppercase text-xs tracking-wide panel text-slate-300 hover:text-[#FF3B30]">Undo Dart</button>
      </div>

      <div className="mt-4 panel rounded-sm p-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Runs by inning</div>
        <div className="flex flex-wrap gap-1">
          {Array.from({ length: BB_MAX_INNING }).map((_, i) => (
            <div key={i} className="w-11 text-center">
              <div className="text-[10px] text-slate-500">{i + 1}</div>
              <div className="font-display text-base font-bold text-[#34C759]">{innings[i] != null ? innings[i] : "·"}</div>
              <div className="font-display text-sm font-bold text-[#8E8E93]">{i < revealed ? ghost.innings[i] : "·"}</div>
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {burst && <Burst label={burst.label} sub={burst.sub} color={burst.color} onDone={() => setBurst(null)} />}
      </AnimatePresence>
    </>
  );
}

function GhostGameResult({ result, cfg, onGameEnd, youLabel, ghostLabel }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="panel rounded-sm p-6 text-center">
      <div className={`inline-flex items-center gap-2 px-4 py-1.5 font-bold uppercase text-xs tracking-widest rounded-sm mb-4 ${result.youWon ? "bg-[#34C759] text-black" : "bg-[#3a3a3c] text-slate-200"}`}>
        {result.youWon ? "You beat the Ghost" : "Ghost got you"}
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{cfg.playerName}</div>
          <div className="font-display text-2xl font-black text-[#34C759]">{youLabel}</div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{ghostOwner(cfg)}</div>
          <div className="font-display text-2xl font-black text-[#8E8E93]">{ghostLabel}</div>
        </div>
      </div>
      <button data-testid="ghost-continue-btn" onClick={() => onGameEnd(result)} className="w-full bg-[#007AFF] hover:bg-[#0069d9] text-white font-bold uppercase tracking-wide py-3.5 rounded-sm transition-colors">
        {cfg.bestOf > 1 ? "Continue Series" : "See Result"}
      </button>
    </motion.div>
  );
}

function fmtDate(d) {
  if (!d) return "an earlier game";
  try {
    return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "an earlier game";
  }
}

function GhostSeriesSummary({ cfg, log, youWins, ghostWins, navigate }) {
  const seriesWon = cfg.bestOf === 1 ? log[0]?.youWon : youWins > ghostWins;
  const isBaseball = cfg.gameType === "baseball";
  return (
    <div className="max-w-2xl mx-auto">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-6">
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 font-bold uppercase text-xs tracking-widest rounded-sm mb-4 ${seriesWon ? "bg-[#34C759] text-black" : "bg-[#3a3a3c] text-slate-200"}`}>
          <Ghost className="w-4 h-4" /> Ghost {cfg.gameType === "baseball" ? "Baseball" : "501"} Complete
        </div>
        <h1 data-testid="ghost-series-result" className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
          {seriesWon ? "You Win" : "Ghost Wins"}
        </h1>
        {cfg.bestOf > 1 && (
          <div className="font-display text-3xl font-bold text-[#007AFF] mt-2">{youWins} — {ghostWins}</div>
        )}
      </motion.div>

      <div className="panel rounded-sm p-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3 flex items-center gap-1.5">
          <Trophy className="w-4 h-4 text-[#FFCC00]" /> The ghosts you raced
        </div>
        <div className="space-y-2">
          {log.map((entry, i) => (
            <div key={i} data-testid={`ghost-reveal-${i}`} className="flex items-center justify-between border-b border-[#1c1c1c] py-2">
              <div>
                <div className="text-sm font-bold text-white">
                  {cfg.bestOf > 1 ? `Game ${entry.gameNo} · ` : ""}{cfg.ghostName ? `${cfg.ghostName}'s` : "Your"} {entry.ghost.source === "match" ? "match" : "practice"} game
                </div>
                <div className="text-xs text-slate-500">{fmtDate(entry.ghost.date)}</div>
              </div>
              <div className="text-right">
                <div className={`font-display text-lg font-bold ${entry.youWon ? "text-[#34C759]" : "text-[#8E8E93]"}`}>
                  {entry.youWon ? "Won" : "Lost"}
                </div>
                <div className="text-xs text-slate-500">
                  {isBaseball ? `${entry.youValue} — ${entry.ghostValue} runs`
                    : `${entry.youValue != null ? entry.youValue + "d" : "—"} vs ${entry.ghostValue}d`}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-600 mt-3">Ghost games are practice only — they never change your wins, rivalries or stats.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <button data-testid="ghost-again-btn" onClick={() => navigate("/practice")} className="flex-1 bg-[#34C759] hover:bg-[#2fb350] text-black font-bold uppercase tracking-wide py-4 rounded-sm transition-colors">
          Practice Again
        </button>
        <button data-testid="ghost-home-btn" onClick={() => navigate("/")} className="flex-1 flex items-center justify-center gap-2 panel rounded-sm py-4 font-bold uppercase tracking-wide text-white hover:border-[#007AFF] transition-colors">
          <Home className="w-5 h-5" /> Return Home
        </button>
      </div>
    </div>
  );
}
